import { Alert, Box, Stack, styled } from '@mui/material';
import {
  TOAST_COLLAPSED_HEIGHT,
  TOAST_COLLAPSED_WIDTHS,
  TOAST_COLLAPSED_X_OFFSETS,
  TOAST_COLLAPSED_Y_OFFSETS,
  TOAST_EXPANDED_GAP,
  TOAST_EXPANDED_WIDTH,
  TOAST_MAX_VISIBLE,
} from 'components/Alert/constants';
import { StyledAlertProps, ToastCollapsedItemProps, ToastStackContainerProps } from 'components/Alert/types';
import { newGreen, newRed, neutral, textSecondary, warningDark, warningLight } from 'themes/colors';

const legacyCommonStyle = `
  position: fixed;
  top: 0;
  z-index: 9999;
  width: 100%;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  line-height: 18px;
  padding: 12px 16px;
  border-radius: 0px;
`;

export const getAlertMaxWidth = (drawerOpen: boolean) =>
  drawerOpen ? 'calc(100vw - 198.67px)' : 'calc(100vw - 63.67px)';

export const StyledSuccessAlert = styled(Alert)<StyledAlertProps>`
  ${legacyCommonStyle}
  background-color: #16a085;
  max-width: ${({ drawerOpen }) => getAlertMaxWidth(drawerOpen)};
`;

export const StyledWarningAlert = styled(Alert)<StyledAlertProps>`
  ${legacyCommonStyle}
  background-color: #f39a79;
  max-width: ${({ drawerOpen }) => getAlertMaxWidth(drawerOpen)};
`;

export const StyledErrorAlert = styled(Alert)<StyledAlertProps>`
  ${legacyCommonStyle}
  background-color: #c44d56;
  max-width: ${({ drawerOpen }) => getAlertMaxWidth(drawerOpen)};
`;

const stackedCommonStyle = {
  borderStyle: 'solid',
  borderWidth: 1,
  borderLeftWidth: 3,
  borderRadius: '8px',
  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.16)',
  padding: '16px',
  width: `${TOAST_EXPANDED_WIDTH}px`,
  maxWidth: '100%',
  '& .MuiAlert-icon': {
    marginRight: '12px',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    fontSize: '20px',
  },
  '& .MuiAlert-message': {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: 'inherit',
    padding: 0,
    width: '100%',
    overflowWrap: 'anywhere',
  },
  '& .MuiAlert-action': {
    margin: 0,
    padding: 0,
    alignSelf: 'flex-start',
  },
  '& .MuiAlert-action .MuiIconButton-root': {
    color: textSecondary,
    padding: '2px',
    width: '24px',
    height: '24px',
    borderRadius: '8px',
  },
};

export const ToastViewport = styled(Stack)(({ theme }) => ({
  position: 'fixed',
  top: '24px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1400,
  alignItems: 'center',
  pointerEvents: 'none',
  [theme.breakpoints.down('md')]: {
    top: '16px',
    left: '12px',
    right: '12px',
    transform: 'none',
    alignItems: 'stretch',
  },
}));

export const StackedSuccessAlert = styled(Alert)(() => ({
  ...stackedCommonStyle,
  color: newGreen.successDark,
  borderColor: newGreen.successDark,
  backgroundColor: newGreen.successLight,
}));

export const StackedWarningAlert = styled(Alert)(() => ({
  ...stackedCommonStyle,
  color: warningDark,
  borderColor: warningDark,
  backgroundColor: warningLight,
}));

export const StackedErrorAlert = styled(Alert)(() => ({
  ...stackedCommonStyle,
  color: newRed.errorDark,
  borderColor: newRed.errorDark,
  backgroundColor: newRed.errorLight,
}));

export const ToastStackContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<ToastStackContainerProps>(({ expanded }) => ({
  width: `${TOAST_EXPANDED_WIDTH}px`,
  maxWidth: '100%',
  position: 'relative',
  minHeight: expanded ? 'auto' : `${TOAST_COLLAPSED_HEIGHT}px`,
  pointerEvents: 'auto',
}));

export const ToastExpandedStack = styled(Stack)(() => ({
  gap: `${TOAST_EXPANDED_GAP}px`,
}));

export const ToastCollapsedItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'stackIndex',
})<ToastCollapsedItemProps>(({ stackIndex }) => ({
  position: 'absolute',
  top: `${TOAST_COLLAPSED_Y_OFFSETS[stackIndex] || 0}px`,
  left: `${TOAST_COLLAPSED_X_OFFSETS[stackIndex] || 0}px`,
  width: `${TOAST_COLLAPSED_WIDTHS[stackIndex] || TOAST_COLLAPSED_WIDTHS[0]}px`,
  zIndex: TOAST_MAX_VISIBLE - stackIndex,
  '& .MuiAlert-action .MuiIconButton-root': {
    color: neutral[400],
  },
}));
